import { createRouter, createWebHistory } from 'vue-router'
import { authGuard, agreementGuard, groupGuard, guestOnlyGuard } from './guards'
import type { ExtendedRouteMeta } from '@/types/route.types'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      component: () => import('@/layouts/AuthLayout.vue'),
      beforeEnter: guestOnlyGuard,
      children: [
        {
          path: '',
          name: 'Login',
          component: () => import('@/views/Login/Login.vue'),
          meta: {
            requiresAuth: false,
            layout: 'auth'
          }
        }
      ]
    },
    {
      path: '/sso',
      component: () => import('@/layouts/AuthLayout.vue'),
      beforeEnter: guestOnlyGuard,
      children: [
        {
          path: '',
          name: 'SsoLogin',
          component: () => import('@/views/Login/Login.vue'),
          meta: {
            requiresAuth: false,
            layout: 'auth',
            autoSSO: true
          }
        }
      ]
    },
    {
      path: '/unauthorized',
      component: () => import('@/layouts/AuthLayout.vue'),
      children: [
        {
          path: '',
          name: 'Unauthorized',
          component: () => import('@/views/Error').then(m => m.Unauthorized),
          meta: {
            requiresAuth: false
          }
        }
      ]
    },
    {
      path: '/',
      component: () => import('@/layouts/LegacyLayout.vue'),
      beforeEnter: [authGuard, agreementGuard, groupGuard],
      children: [
        {
          path: '',
          redirect: '/home'
        },
        {
          path: 'home',
          name: 'Home',
          component: () => import('@/views/Home/Home.vue'),
          meta: {
            title: 'Home',
            icon: 'mdi-home',
            order: 1,
            description: 'Home with overview and statistics',
            showInNav: false
          }
        },
        {
          path: 'dashboards',
          name: 'Dashboards',
          component: () => import('@/views/Dashboards/Dashboards.vue'),
          meta: {
            allowedGroups: ['TurnCareAdmin'],
            title: 'Dashboards',
            icon: 'mdi-view-dashboard',
            order: 3,
            description: 'Manage QuickSight dashboards here. You can add new Dashboards by entering the ARN and activate/deactivate them.',
            showInNav: true,
            breadcrumbParent: 'Admin'
          }
        },
        {
          path: 'groups',
          name: 'Groups',
          component: () => import('@/views/Groups/Groups.vue'),
          meta: {
            allowedGroups: ['TurnCareAdmin'],
            title: 'Groups',
            icon: 'mdi-account-group',
            order: 4,
            description: 'Manage user groups here. You can assign multiple groups to a user, which determines what actions they can perform.',
            showInNav: true,
            breadcrumbParent: 'Admin'
          }
        },
        {
          path: 'roles',
          name: 'Roles',
          component: () => import('@/views/Roles/Roles.vue'),
          meta: {
            allowedGroups: ['TurnCareAdmin', 'CustomerAdmin'], // Only admin group can access roles
            title: 'Roles',
            icon: 'mdi-shield-account',
            order: 5,
            description: 'Configure user roles and access levels. You can assign multiple roles to a user, and decide which Dashboards they can access.',
            customerDescription: 'Manage user roles here. You can only view roles and see which Dashboards are available for each roles.',
            showInNav: true,
            breadcrumbParent: 'Admin'
          }
        },
        {
          path: 'facilities',
          name: 'Facilities',
          component: () => import('@/views/Facilities'),
          meta: {
            allowedGroups: ['TurnCareAdmin'],
            title: 'Facilities',
            icon: 'mdi-domain',
            order: 6,
            description: 'Manage system facilities and deployments. You can setup evaluation start and end dates, and determine which facilities are Admins.',
            showInNav: true,
            breadcrumbParent: 'Admin'
          }
        },
        {
          path: 'users',
          name: 'Users',
          component: () => import('@/views/Users/Users.vue'),
          meta: {
            allowedGroups: ['TurnCareAdmin', 'CustomerAdmin'], // Only admin group can access users
            title: 'Users',
            icon: 'mdi-account-multiple',
            order: 7,
            description: 'Manage user login access here. You can manage users and assign them roles. You can view which users are assigned to which facilities.',
            customerDescription: 'Manage user login access here. You can only view users and assign them roles. You can view which users are assigned to which facilities.',
            showInNav: true,
            breadcrumbParent: 'Admin'
          }
        },
        {
          path: 'logs',
          name: 'Logs',
          component: () => import('@/views/Logs/Logs.vue'),
          meta: {
            allowedGroups: ['TurnCareAdmin', 'CustomerAdmin'],
            title: 'Logs',
            icon: 'mdi-text-box-multiple',
            order: 8,
            description: 'View site logs here. Details of login, user provisioning, asset modifications, and deletions will be captured here. Export is also available.',
            showInNav: true,
            breadcrumbParent: 'Admin'
          }
        },
        {
          path: 'admin',
          name: 'Admin',
          component: () => import('@/views/Admin/Admin.vue'),
          meta: {
            allowedGroups: ['TurnCareAdmin', 'CustomerAdmin'],
            title: 'TurnCare Admin',
            icon: 'mdi-shield-crown',
            order: 2,
            description: 'Administrative overview of all system pages and features',
            showInNav: false
          }
        },
        {
          path: 'dashboard/:id',
          name: 'DashboardEmbed',
          component: () => import('@/views/DashboardEmbed/DashboardEmbed.vue'),
          meta: {
            title: 'Dashboard',
            icon: 'mdi-view-dashboard',
            showInNav: false,
            showBackButton: true,
            description: 'Embedded dashboard view'
          }
        },
      ]
    },
    {
      path: '/:pathMatch(.*)*',
      component: () => import('@/layouts/AuthLayout.vue'),
      children: [
        {
          path: '',
          name: 'NotFound',
          component: () => import('@/views/Error').then(m => m.NotFound)
        }
      ]
    }
  ]
})

export default router
